/**
 * Public landing page for shared construction files.
 *
 * Reached via `/share/:token`. Anyone with the link can view the file's
 * metadata (name, size, project) and download it — no login required.
 * The token itself is opaque and rate-limited server-side; if it's been
 * revoked or has expired the API replies with 410 and we render a
 * friendly error state.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Download,
  Loader2,
  FileText,
  Image as ImageIcon,
  ShieldAlert,
  Clock,
  Package,
  Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { humanFileSize } from "../lib/constructionFileMeta";

// Kept local — a public landing page shouldn't leak any tenant-specific
// wiring or auth headers into fetch calls.
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function formatExpiry(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("tr-TR", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function PublicShare() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/share/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || "Bağlantı geçersiz");
        }
        const data = await res.json();
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (err) {
        if (!cancelled) setState({ loading: false, data: null, error: err.message || "Bir hata oluştu" });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const doDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/share/${token}/download`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "İndirilemedi");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = state.data?.file?.original_filename || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setState((s) => ({ ...s, error: err.message || "İndirme başarısız" }));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 via-white to-violet-50 px-4 py-12">
      <div className="mx-auto flex max-w-md flex-col items-center">
        {/* Brand strip */}
        <div className="mb-6 flex items-center gap-2 text-sm font-semibold text-zinc-500">
          <Sparkles className="h-4 w-4 text-violet-600" />
          <span className="tracking-wide">Serpix · Paylaşılan dosya</span>
        </div>

        <div
          data-testid="public-share-card"
          className="w-full overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-xl"
        >
          {state.loading ? (
            <div className="flex flex-col items-center gap-3 p-12">
              <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
              <div className="text-sm text-zinc-500">Bağlantı doğrulanıyor…</div>
            </div>
          ) : state.error ? (
            <ErrorState message={state.error} />
          ) : (
            <ValidState data={state.data} downloading={downloading} onDownload={doDownload} />
          )}
        </div>

        <p className="mt-6 max-w-sm text-center text-[11px] leading-relaxed text-zinc-400">
          Bu bağlantı Serpix üzerinden oluşturuldu. Dosyaya sadece bu linke
          sahip olanlar erişebilir; süresi dolduğunda otomatik olarak
          devre dışı kalır.
        </p>
      </div>
    </div>
  );
}

function ValidState({ data, downloading, onDownload }) {
  const file = data.file || {};
  const project = data.project || {};
  const isImage = (file.content_type || "").startsWith("image/");
  const isPdf = file.content_type === "application/pdf";
  const Icon = isImage ? ImageIcon : isPdf ? FileText : Package;

  return (
    <>
      <div className="flex flex-col items-center gap-3 border-b border-zinc-100 bg-gradient-to-b from-zinc-50 to-white p-8">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
          <Icon className="h-8 w-8" />
        </div>
        <div className="text-center">
          <div
            className="text-base font-semibold text-zinc-900"
            data-testid="public-share-filename"
          >
            {file.original_filename || "Dosya"}
          </div>
          <div className="mt-1 text-[12px] text-zinc-500">
            {humanFileSize(file.size)} · {file.content_type || "—"}
          </div>
        </div>
      </div>

      <div className="space-y-2 px-6 py-5 text-sm">
        {project.company_name && (
          <MetaRow
            icon={Package}
            label="Proje"
            value={
              project.stand_no
                ? `${project.company_name} · #${project.stand_no}`
                : project.company_name
            }
          />
        )}
        <MetaRow
          icon={Clock}
          label="Son geçerlilik"
          value={formatExpiry(data.expires_at)}
        />
      </div>

      <div className="border-t border-zinc-100 bg-zinc-50 px-6 py-4">
        <Button
          onClick={onDownload}
          disabled={downloading}
          data-testid="public-share-download-btn"
          className="w-full bg-violet-600 text-white hover:bg-violet-700"
        >
          {downloading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Dosyayı indir
        </Button>
      </div>
    </>
  );
}

function ErrorState({ message }) {
  return (
    <div className="flex flex-col items-center gap-3 p-12 text-center" data-testid="public-share-error">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-rose-600">
        <ShieldAlert className="h-7 w-7" />
      </div>
      <div className="text-base font-semibold text-zinc-900">
        Bağlantıya erişilemiyor
      </div>
      <div className="max-w-xs text-sm text-zinc-500">{message}</div>
    </div>
  );
}

function MetaRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-500">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="truncate text-[13px] text-zinc-800">{value}</div>
      </div>
    </div>
  );
}
